import * as React from "react";

// Metric renders one chemistry reading against a grow's ideal band.
// current/ideal/min/max all arrive in storage units (Celsius, mS/cm, gal);
// `display` maps a stored value into the user's preferred unit. The delta is
// computed in display space (after the conversion) so an affine unit like
// temperature doesn't carry its +32 offset into the difference. In-range is
// judged against the stored min/max band. Shared by the grow detail Overview,
// the dashboard grow-card, and the reading-capture card.
export function Metric({
  icon,
  label,
  current,
  ideal,
  min,
  max,
  suffix = "",
  display = (n) => n,
  digits,
}: {
  icon: React.ReactNode;
  label: string;
  current?: number;
  ideal?: number;
  min?: number;
  max?: number;
  suffix?: string;
  display?: (n: number) => number;
  digits: number;
}) {
  const has = current !== undefined && Number.isFinite(current);
  const inRange =
    has && min !== undefined && max !== undefined
      ? current! >= min && current! <= max
      : undefined;
  const delta =
    has && ideal !== undefined && Number.isFinite(ideal)
      ? display(current!) - display(ideal)
      : undefined;

  return (
    <div className="rounded-md border bg-muted/30 px-2.5 py-2">
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        <span>{label}</span>
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span
          className={
            "text-lg font-semibold tabular-nums " +
            (inRange === false ? "text-amber-600 dark:text-amber-400" : "")
          }
        >
          {has ? display(current!).toFixed(digits) + suffix : "—"}
        </span>
        {/* hide deltas that round to 0 at the displayed precision */}
        {delta !== undefined && Math.abs(delta) >= 0.5 * 10 ** -digits && (
          <span className="text-xs tabular-nums text-muted-foreground">
            {delta > 0 ? "+" : "−"}
            {Math.abs(delta).toFixed(digits)}
          </span>
        )}
      </div>
    </div>
  );
}
